// # Master Database Component for Admin
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import DashboardHeader from '../../../components/DashboardHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { auth, db } from '../../../auth/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import cacheManager from '../../../auth/cache_manager';
import './master_database.css';

const MasterDatabase = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (auth.currentUser?.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, []);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (auth.currentUser?.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', auth.currentUser.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${auth.currentUser.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${auth.currentUser.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Add admin access check logging
  React.useEffect(() => {
    if (typeof isAdmin === 'undefined') return; // Skip if admin status not yet determined
    
    const startTime = performance.now();
    console.log('Master Database - Admin access check:', {
      operation: 'admin_check',
      isAdmin: !!isAdmin,
      timestamp: new Date().toISOString(),
      timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
    });

    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]); // Only recheck when isAdmin changes

  // # Database options
  const databaseOptions = [
    {
      title: 'USMLE Step 1 Database',
      icon: require('../../../aneo_media/mentee/one.png'),
      path: '/admin/content/step1'
    },
    {
      title: 'USMLE Step 2 Database',
      icon: require('../../../aneo_media/mentee/two.png'),
      path: '/admin/content/step2'
    }
  ];

  return (
    <div className={`mstr_db_admin-page ${isDarkMode ? 'mstr_db_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="mstr_db_admin-content">
        <div className="mstr_db_admin-header">
          <h1>Master Database</h1>
        </div>

        <div className="mstr_db_database-options">
          {databaseOptions.map((option, index) => (
            <div 
              key={index}
              className="mstr_db_database-card"
              onClick={() => navigate(option.path)}
            >
              <div className="mstr_db_card-icon">
                <img src={option.icon} alt={option.title} />
              </div>
              <h3>{option.title}</h3>
            </div>
          ))}
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default MasterDatabase; 